import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { CreditCard, Building, Phone, DollarSign, Save, Loader2, Info } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPaymentSettings = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    bank_name: '',
    account_holder: '',
    account_number: '',
    routing_number: '',
    zelle_phone: '',
    paypal_username: '',
    venmo_username: '',
    instructions: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/payment-settings`, getAuthHeaders());
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching payment settings:', error);
      toast.error('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.post(`${API_URL}/admin/payment-settings`, settings, getAuthHeaders());
      toast.success('Payment settings saved successfully!');
    } catch (error) {
      console.error('Error saving payment settings:', error);
      toast.error('Failed to save payment settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-saffron-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Payment Settings</h1>
        <p className="text-gray-600 mt-1">Configure payment options for customers to pay manually</p>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-800 font-medium">How Manual Payments Work</p>
              <p className="text-blue-700 text-sm mt-1">
                When customers choose manual payment at checkout, they will see the payment details you configure here. 
                They can then transfer money to your account and you'll process their order after confirming payment.
                <strong className="block mt-1">Note: Your email will NOT be displayed to customers.</strong>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Bank Transfer Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="w-5 h-5 text-saffron-600" />
              Bank Transfer Details
            </CardTitle>
            <CardDescription>
              Enter your bank account information for direct transfers
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bank_name">Bank Name</Label>
                <Input
                  id="bank_name"
                  name="bank_name"
                  placeholder="e.g., Chase Bank, Bank of America"
                  value={settings.bank_name}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="account_holder">Account Holder Name</Label>
                <Input
                  id="account_holder"
                  name="account_holder"
                  placeholder="Name on the account"
                  value={settings.account_holder}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="account_number">Account Number</Label>
                <Input
                  id="account_number"
                  name="account_number"
                  placeholder="Your account number"
                  value={settings.account_number}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="routing_number">Routing Number</Label>
                <Input
                  id="routing_number"
                  name="routing_number"
                  placeholder="Bank routing number"
                  value={settings.routing_number}
                  onChange={handleChange}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Digital Payment Options */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-saffron-600" />
              Digital Payment Options
            </CardTitle>
            <CardDescription>
              Add Zelle, PayPal, or Venmo for quick digital payments
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="zelle_phone">Zelle Phone/Email</Label>
                <Input
                  id="zelle_phone"
                  name="zelle_phone"
                  placeholder="Phone or email for Zelle"
                  value={settings.zelle_phone}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="paypal_username">PayPal Username/Email</Label>
                <Input
                  id="paypal_username"
                  name="paypal_username"
                  placeholder="PayPal username or email"
                  value={settings.paypal_username}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="venmo_username">Venmo Username</Label>
                <Input
                  id="venmo_username"
                  name="venmo_username"
                  placeholder="@username"
                  value={settings.venmo_username}
                  onChange={handleChange}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Instructions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-saffron-600" />
              Payment Instructions
            </CardTitle>
            <CardDescription>
              Add any special instructions for customers making payments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="instructions">Instructions (shown to customers)</Label>
              <Textarea
                id="instructions"
                name="instructions"
                placeholder="e.g., Please include your order number in the payment memo. Allow 1-2 business days for payment confirmation."
                value={settings.instructions}
                onChange={handleChange}
                rows={4}
              />
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            type="submit" 
            disabled={saving}
            className="bg-saffron-500 hover:bg-saffron-600"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Payment Settings
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminPaymentSettings;
